import api from './api';

import { 
  SerialNumberResponse, 
  PartGroupsResponse,
  PartGroup,
  YedekParcaResponse,
  ServiceRequestData,
  ServiceRequestCreateResponse,
  ServiceRequestSparePartData,
  ServiceRequestSparePartCreateResponse,
  IssueReasonsResponse,
  UsedWarrantyPart
} from '../types/ServiceRequest';

/**
 * Seri numarasını kontrol eder ve ilgili ürün bilgilerini getirir
 * @param serialNumber Kontrol edilecek seri numarası
 * @returns Ürün bilgileri veya boş dizi
 */
export const checkSerialNumber = async (serialNumber: string): Promise<SerialNumberResponse> => {
  const formData = new URLSearchParams();
  formData.append('model', 'Talepler');
  formData.append('store', 'Kuponlar');
  formData.append('limitless', 'false');
  formData.append('query', serialNumber);
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '1');

  const response = await api.post<SerialNumberResponse>('/json', formData);
  return response.data;
};

/**
 * Belirli bir kategori için parça gruplarını getirir
 * @param categoryName Ürünün kategori adı
 * @returns Parça grupları listesi veya boş dizi
 */
export const getPartGroups = async (categoryName: string): Promise<PartGroupsResponse> => {
  const formData = new URLSearchParams();
  formData.append('store', 'Gruplar');
  formData.append('model', 'Talepler');
  formData.append('kategori', categoryName);
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '25');

  const response = await api.post<PartGroupsResponse>('/json', formData);
  return response.data;
};

/**
 * Arıza nedenlerini getirir
 * @returns Arıza nedeni listesi
 */
export const getIssueReasons = async (): Promise<IssueReasonsResponse> => {
  const formData = new URLSearchParams();
  formData.append('store', 'ArizaNedenleri');
  formData.append('limitless', 'true');
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '25');

  const response = await api.post<IssueReasonsResponse>('/json', formData);
  return response.data;
};

/**
 * Yedek parça araması yapar
 * @param searchTerm Aranacak parça adı
 * @param partGroup Parça grubu
 * @param model Ürün model numarası
 * @returns Yedek parça listesi
 */
export const searchYedekParca = async (searchTerm: string, partGroup: PartGroup, model: number): Promise<YedekParcaResponse> => {
  // Her çağrıda benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'YedekParcalar');
  formData.append('fields', 'Kod,Ad,Gorsel,GrupAdi,Stok,CariStokDurumu,ListeFiyati,BayiFiyati,IscilikBedeli,PromosyonluMu,AktifMi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('prefilter', JSON.stringify([{"type":"boolean","field":"AktifMi","value":true}]));
  formData.append('modelparams', '0');
  formData.append('modelparams', model.toString());
  formData.append('modelparams', partGroup.Id.toString());
  
  // Arama terimi boş değilse filtre ekle
  if (searchTerm.trim()) {
    formData.append('filter', JSON.stringify([{"type":"string","value":searchTerm,"field":"Ad"}]));
  } else {
    // Boş filtre ekle (tüm kayıtları döndürür)
    formData.append('filter', JSON.stringify([]));
  }
  
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '50');
  formData.append('sort', 'Kod');
  formData.append('dir', 'ASC');

  try {
    const response = await api.post<YedekParcaResponse>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Yedek parça arama hatası:', error);
    throw error;
  }
};

/**
 * Servis talep numarası alır
 * @returns Talep numarası
 */
export const getRequestNumber = async (): Promise<string> => {
  const formData = new URLSearchParams();
  formData.append('store', 'No');
  formData.append('model', 'Talepler');
  formData.append('limitless', 'true');

  try {
    const response = await api.post<{data: [{No: string}], total: number}>('/json', formData);
    
    if (response.data && response.data.data && response.data.data.length > 0) {
      return response.data.data[0].No;
    }
    
    throw new Error('Talep numarası alınamadı');
  } catch (error) {
    console.error('Talep numarası alma hatası:', error);
    throw error;
  }
};

/**
 * Servis talebi oluşturur
 * @param requestData Servis talebi verileri
 * @returns Oluşturulan talep ID'si
 */
export const createServiceRequest = async (requestData: ServiceRequestData): Promise<ServiceRequestCreateResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  // İstek için formData oluştur
  const formData = new URLSearchParams();
  formData.append('storeId', storeId);
  formData.append('model', 'Talepler');
  formData.append('fromView', 'true');
  
  // Veriyi string olarak gönder
  formData.append('data', JSON.stringify(requestData));

  try {
    const response = await api.post<ServiceRequestCreateResponse>('/data/create', formData);
    return response.data;
  } catch (error) {
    console.error('Servis talebi oluşturma hatası:', error);
    throw error;
  }
};

/**
 * Talep yedek parçası oluşturur
 * @param sparePartData Yedek parça verileri
 * @returns Oluşturulan yedek parça ID'si
 */
export const createServiceRequestSparePart = async (sparePartData: ServiceRequestSparePartData): Promise<ServiceRequestSparePartCreateResponse> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  // İstek için formData oluştur
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'true');
  formData.append('storeId', storeId);
  formData.append('model', 'TalepYedekParcalari');
  formData.append('fields', 'Talep,YedekParca,Kod,Ad,GrupAdi,Gorsel,Miktar,IscilikBedeli,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', 'Talep');
  formData.append('masterid', sparePartData.Talep.toString());
  
  // Veriyi string olarak gönder
  formData.append('data', JSON.stringify(sparePartData));

  try {
    const response = await api.post<ServiceRequestSparePartCreateResponse>('/data/create', formData);
    return response.data;
  } catch (error) {
    console.error('Talep yedek parçası oluşturma hatası:', error);
    throw error;
  }
};

/**
 * Talep yedek parçalarını getirir
 * @param serviceRequestId Servis talebi ID'si
 * @returns Yedek parça listesi
 */
export const getServiceRequestSpareParts = async (serviceRequestId: string): Promise<{ data: any[]; total: number }> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  // İstek için formData oluştur
  const formData = new URLSearchParams();
  formData.append('ignoreclient', 'true');
  formData.append('isTree', 'false');
  formData.append('limitless', 'true');
  formData.append('storeId', storeId);
  formData.append('model', 'TalepYedekParcalari');
  formData.append('fields', 'Talep,YedekParca,Kod,Ad,Gorsel,GrupAdi,Stok,CariStokDurumu,ListeFiyati,BayiFiyati,IscilikBedeli,PromosyonluMu,AktifMi,Miktar,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', 'Talep');
  formData.append('masterid', serviceRequestId);
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '50');

  try {
    const response = await api.post<{ data: any[]; total: number }>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Talep yedek parçaları alma hatası:', error);
    throw error;
  }
}; 

/**
 * Servis talebi detaylarını getirir
 * @param serviceRequestId Servis talebi ID'si
 * @returns Servis talebi detayları
 */
export const getServiceRequestDetail = async (serviceRequestId: string): Promise<any> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('ignoreclient', 'true');
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Talepler');
  formData.append('fields', 'Kupon,No,Cari,CariAdi,Tip,TipAdi,Km,ArizaNedeni,ArizaNedeniAdi,SonEtkinlik,SonEtkinlikTipi,KuponSonIslemAdi,SiparisVarMi,SiparisNo,KategoriAdi,MarkaAdi,Model,ModelAdi,RenkAdi,Sasi,Motor,MotorMu,FaturaNo,SaticiAdi,SaticiTelefon,AliciAdi,AliciTelefon,SonIslem,IslemAdi,YedekParcaVarMi,GorselVarMi,OdendiMi,Tarih,EtkinlikTarihi,SatisTarihi,IslemTarihi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('filter', JSON.stringify([{"type":"number","value":parseInt(serviceRequestId),"field":"Id"}]));
  formData.append('page', '1');
  formData.append('start', '0');
  formData.append('limit', '50');
  formData.append('sort', 'No');
  formData.append('dir', 'DESC');

  try {
    const response = await api.post<any>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Servis talebi detayı alma hatası:', error);
    throw error;
  }
}; 

/**
 * Servis taleplerini getirir
 * @param page Sayfa numarası
 * @param limit Sayfa başına kayıt sayısı
 * @param statusFilter Durum filtresi
 * @param dateFilter Tarih filtresi
 * @returns Servis talepleri listesi
 */
export const getServiceRequests = async (
  page: number = 1,
  limit: number = 20,
  statusFilter?: string[] | any[],
  dateFilter?: { startDate?: string; endDate?: string }
): Promise<{ data: any[]; total: number }> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Talepler');
  formData.append('fields', 'Kupon,No,Cari,CariAdi,Tip,TipAdi,Km,ArizaNedeni,ArizaNedeniAdi,SonEtkinlik,SonEtkinlikTipi,KuponSonIslemAdi,SiparisVarMi,SiparisNo,KategoriAdi,MarkaAdi,Model,ModelAdi,RenkAdi,Sasi,Motor,MotorMu,FaturaNo,SaticiAdi,SaticiTelefon,AliciAdi,AliciTelefon,SonIslem,IslemAdi,YedekParcaVarMi,GorselVarMi,OdendiMi,Tarih,EtkinlikTarihi,SatisTarihi,IslemTarihi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('page', page.toString());
  formData.append('start', ((page - 1) * limit).toString());
  formData.append('limit', limit.toString());
  formData.append('sort', 'No');
  formData.append('dir', 'DESC');

  // Filtreler
  const filters: any[] = [];
  
  // Durum filtresi
  if (statusFilter && statusFilter.length > 0) {
    // Eğer statusFilter array'inin ilk elemanı obje ise, direkt filtre olarak kullan
    if (typeof statusFilter[0] === 'object' && statusFilter[0] !== null) {
      filters.push(...statusFilter);
    } else {
      // String array ise eski mantığı kullan
      filters.push({
        type: 'list',
        value: statusFilter,
        field: 'IslemAdi'
      });
    }
  }
  
  // Tarih filtresi
  if (dateFilter) {
    if (dateFilter.startDate) {
      filters.push({
        type: 'date',
        comparison: 'gt',
        value: dateFilter.startDate,
        field: 'Tarih'
      });
    }
    
    if (dateFilter.endDate) {
      filters.push({
        type: 'date',
        comparison: 'lt',
        value: dateFilter.endDate,
        field: 'Tarih'
      });
    }
  }

  if (filters.length > 0) {
    formData.append('filter', JSON.stringify(filters));
  }

  try {
    const response = await api.post<{ data: any[]; total: number }>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Servis talepleri yükleme hatası:', error);
    throw error;
  }
};

/**
 * Servis talebi siler
 * @param serviceRequestId Silinecek servis talebi ID'si
 * @returns Silme işlemi sonucu
 */
/**
 * Servis taleplerinde arama yapar
 * @param query Arama terimi
 * @param searchType Arama tipi ('serial' | 'invoice')
 * @param page Sayfa numarası
 * @param limit Sayfa başına kayıt sayısı
 * @returns Arama sonuçları
 */
export const searchServiceRequests = async (
  query: string,
  searchType: 'serial' | 'invoice',
  page: number = 1,
  limit: number = 20
): Promise<{ data: any[]; total: number }> => {
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('ignoreclient', 'true');
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Talepler');
  formData.append('fields', 'Kupon,No,Cari,CariAdi,Tip,TipAdi,Km,ArizaNedeni,ArizaNedeniAdi,SonEtkinlik,SonEtkinlikTipi,KuponSonIslemAdi,SiparisVarMi,SiparisNo,KategoriAdi,MarkaAdi,Model,ModelAdi,RenkAdi,Sasi,Motor,MotorMu,FaturaNo,SaticiAdi,SaticiTelefon,AliciAdi,AliciTelefon,SonIslem,IslemAdi,YedekParcaVarMi,GorselVarMi,OdendiMi,Tarih,EtkinlikTarihi,SatisTarihi,IslemTarihi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  formData.append('page', page.toString());
  formData.append('start', ((page - 1) * limit).toString());
  formData.append('limit', limit.toString());
  formData.append('sort', 'No');
  formData.append('dir', 'DESC');

  // Arama tipine göre filter ekle
  const searchFilters = [];
  
  if (searchType === 'serial') {
    // Seri numarası için No alanında arama
    searchFilters.push({
      "type": "string",
      "value": query,
      "field": "Sasi"
    });
  } else if (searchType === 'invoice') {
    // Fatura numarası için FaturaNo alanında arama
    searchFilters.push({
      "type": "string",
      "value": query,
      "field": "FaturaNo"
    });
  }

  if (searchFilters.length > 0) {
    formData.append('filter', JSON.stringify(searchFilters));
  }

  try {
    const response = await api.post<{ data: any[]; total: number }>('/data/read', formData);
    return response.data;
  } catch (error) {
    console.error('Servis talebi arama hatası:', error);
    throw error;
  }
};

export const deleteServiceRequest = async (serviceRequestId: number): Promise<{success: boolean}> => {
  // Benzersiz bir storeId oluştur
  const uniqueId = Date.now();
  const storeId = `store-${uniqueId}`;
  
  const formData = new URLSearchParams();
  formData.append('isTree', 'false');
  formData.append('limitless', 'false');
  formData.append('storeId', storeId);
  formData.append('model', 'Talepler');
  formData.append('fields', 'Kupon,No,Cari,CariAdi,Tip,TipAdi,Km,ArizaNedeni,ArizaNedeniAdi,SonEtkinlik,SonEtkinlikTipi,KuponSonIslemAdi,SiparisVarMi,SiparisNo,KategoriAdi,MarkaAdi,Model,ModelAdi,RenkAdi,Sasi,Motor,MotorMu,FaturaNo,SaticiAdi,SaticiTelefon,AliciAdi,AliciTelefon,SonIslem,IslemAdi,YedekParcaVarMi,GorselVarMi,OdendiMi,Tarih,EtkinlikTarihi,SatisTarihi,IslemTarihi,Id');
  formData.append('writemodel', '');
  formData.append('fromview', 'true');
  formData.append('masterfield', '');
  formData.append('masterid', '0');
  
  // Silme için gerekli data - serviceRequestId'yi içeren minimal data
  const deleteData = {
    Id: serviceRequestId
  };
  
  formData.append('data', JSON.stringify(deleteData));

  try {
    const response = await api.post<{success: boolean}>('/data/destroy', formData);
    return response.data;
  } catch (error) {
    console.error('Servis talebi silme hatası:', error);
    throw error;
  }
};

export interface CheckUsedWarrantyPartsOptions {
  sparePartIds: number[];
  kuponId: number;
  talepId?: number;
  talepTarihi?: string;
}

/**
 * Seçilen yedek parçaların aynı kupondaki daha eski taleplerde kullanılıp kullanılmadığını kontrol eder
 */
export const checkUsedWarrantyParts = async (
  opts: CheckUsedWarrantyPartsOptions
): Promise<UsedWarrantyPart[]> => {
  const formData = new URLSearchParams();
  formData.append('store', 'KullanilmisYedekParcalar');
  formData.append('ids', JSON.stringify(opts.sparePartIds));
  formData.append('kupon', String(opts.kuponId || 0));
  formData.append('id', String(opts.talepId || 0));
  formData.append('tarih', opts.talepTarihi || '');
  formData.append('limitless', 'true');

  const response = await api.post<{ data: UsedWarrantyPart[] }>('/json', formData);
  return response.data?.data || [];
}; 